import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useDateFilterVersion } from "@/lib/useDateFilterVersion";
import { X } from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtINR = (n) => {
  if (n === null || n === undefined || n === "" || Number(n) === 0) return "";
  return Number(n).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

export default function TrialBalanceView({ onClose }) {
  const dfv = useDateFilterVersion();
  const { user } = useAuth();
  const [stack, setStack] = useState([]);
  const [selected, setSelected] = useState(0);
  const [busy, setBusy] = useState(false);

  const company = user?.company_name || "";
  const level = stack[stack.length - 1];

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/trial-balance`);
        setStack([{ title: "Trial Balance", heading: null, ...res.data }]);
      } catch {}
    })();
  }, [dfv]);

  const drill = useCallback(async (row) => {
    if (!row || !row.drill || busy) return;
    setBusy(true);
    try {
      let res;
      if (row.drill === "group") {
        res = await axios.get(`${API}/group-summary/${encodeURIComponent(row.name)}`);
      } else if (row.drill === "party") {
        res = await axios.get(`${API}/party-months`, { params: { side: row.side, party: row.party } });
      } else if (row.drill === "bills") {
        res = await axios.get(`${API}/party-bills`, { params: { side: row.side, party: row.party, month: row.month } });
      }
      if (res) {
        setStack((s) => [...s, { title: "Group Summary", ...res.data }]);
        setSelected(0);
      }
    } catch {}
    setBusy(false);
  }, [busy]);

  const goBack = useCallback(() => {
    setStack((s) => {
      if (s.length <= 1) { onClose(); return s; }
      setSelected(0);
      return s.slice(0, -1);
    });
  }, [onClose]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); goBack(); }
      else if (e.key === "ArrowDown") { e.preventDefault(); setSelected((s) => Math.min(s + 1, (level?.rows?.length || 1) - 1)); }
      else if (e.key === "ArrowUp") { e.preventDefault(); setSelected((s) => Math.max(s - 1, 0)); }
      else if (e.key === "Enter") { e.preventDefault(); drill(level?.rows?.[selected]); }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [goBack, drill, level, selected]);

  if (!level) return <div className="w-full" data-testid="trial-balance-view" />;

  return (
    <div className="w-full" data-testid="trial-balance-view">
      <div className="w-full overflow-hidden shadow-xl flex flex-col" style={{ background: "#fbf7ee", border: "1px solid #e7dcc3", height: "calc(100vh - 0.5rem)" }}>
      {/* Title bar */}
      <div className="flex items-center justify-between px-3 py-1.5 text-white" style={{ background: "#0f766e" }}>
        <span className="text-sm font-bold" data-testid="tb-title">{level.title}</span>
        <span className="text-sm font-black uppercase tracking-wide" data-testid="tb-company-title">{company}</span>
        <button onClick={goBack} data-testid="tb-close-btn" className="p-1 mr-12 hover:bg-white/20 rounded-sm">
          <X size={16} weight="bold" />
        </button>
      </div>

      {/* Header */}
      <div className="flex border-b-2 border-slate-700">
        <div className="flex-1 px-4 py-2 flex items-end">
          <span className="text-sm font-black tracking-[0.3em] text-slate-800" data-testid="tb-particulars-header">Particulars</span>
        </div>
        <div className="w-[300px] border-l border-slate-500">
          {level.heading && (
            <div className="text-center text-sm italic font-semibold text-slate-800 border-b border-slate-400 py-0.5" data-testid="tb-heading">{level.heading}</div>
          )}
          <div className="text-center text-sm font-black text-slate-900 border-b border-slate-400 py-0.5" data-testid="tb-company-header">{company}</div>
          <div className="text-center text-sm font-black text-slate-900 border-b border-slate-400 py-0.5">Closing Balance</div>
          <div className="flex text-xs font-bold text-slate-800">
            <div className="flex-1 text-center py-0.5 border-r border-slate-400" data-testid="tb-debit-header">Debit</div>
            <div className="flex-1 text-center py-0.5" data-testid="tb-credit-header">Credit</div>
          </div>
        </div>
      </div>

      {/* Rows */}
      <div className="flex-1 overflow-y-auto" data-testid="tb-rows">
        {level.rows.map((g, idx) => (
          <div key={`${g.name}-${idx}`} onMouseEnter={() => setSelected(idx)} onClick={() => drill(g)}
            className={`flex items-center cursor-pointer ${selected === idx ? "bg-teal-600" : "hover:bg-[#f1e9d6]"}`}
            data-testid={`tb-row-${g.name}`}>
            <div className={`flex-1 px-4 py-0.5 text-sm font-bold ${selected === idx ? "text-white" : "text-slate-900"}`}>{g.name}</div>
            <div className={`w-[300px] flex text-sm font-semibold ${selected === idx ? "text-white" : "text-slate-900"}`}>
              <div className="flex-1 text-right pr-3 py-0.5" data-testid={`tb-debit-${g.name}`}>{fmtINR(g.debit)}</div>
              <div className="flex-1 text-right pr-3 py-0.5" data-testid={`tb-credit-${g.name}`}>{fmtINR(g.credit)}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Grand Total */}
      <div className="flex items-center border-t-2 border-slate-700 bg-[#f1e9d6]" data-testid="tb-grand-total">
        <div className="flex-1 px-4 py-1.5 text-sm font-black tracking-[0.25em] text-slate-900">Grand Total</div>
        <div className="w-[300px] flex text-sm font-black text-slate-900">
          <div className="flex-1 text-right pr-3 py-1.5" data-testid="tb-grand-debit">{fmtINR(level.grand_total?.debit)}</div>
          <div className="flex-1 text-right pr-3 py-1.5" data-testid="tb-grand-credit">{fmtINR(level.grand_total?.credit)}</div>
        </div>
      </div>
      </div>
    </div>
  );
}
